import { orderBy } from 'lodash';
import { action, computed, observable, set } from 'mobx';
import { IBaseAction, IBasicCommandsActionValue } from './action';
import { IBaseFlowContext, TCommand } from '../../types/builder.types';
import { FlowAction } from './flow-action';
import type { RootStore } from '~/stores/root.store';
import { Flow } from '../../models/flow.model';
import { calculateMultiStageActionId } from './actions.utils';
import { CommandsRelation, mergeCommands } from '../../utils/commands.utils';

export interface IActionStage<TContext extends IBaseFlowContext> {
    createCommands: () => void;
    applySideEffects?: () => void;
    revertSideEffects?: () => void;
    contextOverride?: Partial<TContext>;
    stageCommandsRelation?: CommandsRelation;
    order: number;
}

export interface IMultiStageAction<TContext extends IBaseFlowContext>
    extends IBaseAction<TContext> {
    stages: Record<string, IActionStage<TContext>>;
}

export interface MultiStageFlowAction<TContext extends IBaseFlowContext>
    extends IMultiStageAction<TContext> {}

export class MultiStageFlowAction<
    TContext extends IBaseFlowContext = { flow: Flow }
> extends FlowAction<TContext> {
    lastCreatedCommands: Partial<IBasicCommandsActionValue>;
    @observable lastCreatedStageName: string;
    @observable lastAppliedStageName: string;
    @observable itemId: string;

    constructor(rootStore: RootStore) {
        super(rootStore);

        this.stages = {};
        this.lastCreatedCommands = {};

        this.createCommands = () => {
            this.createAllStagesCommands();

            super.applyCommands();
        };
    }

    @computed
    get id(): string {
        if (this.itemId) {
            return calculateMultiStageActionId({
                itemId: this.itemId,
                pageId: this.ctx.pageId,
                actionName: this.name
            });
        }

        return calculateMultiStageActionId({
            blockId: (this.ctx as any).blockId,
            pageId: this.ctx.pageId,
            actionName: this.name
        });
    }

    @computed
    get lastCreatedStage() {
        return this.stages[this.lastCreatedStageName];
    }

    @computed
    get lastAppliedStage() {
        return this.stages[this.lastAppliedStageName];
    }

    initMultiStage(actionData: IMultiStageAction<TContext>) {
        set(this, actionData);
    }

    @action
    setItemId(itemId: string) {
        this.itemId = itemId;
    }

    createAllStagesCommands() {
        const sortedActionCreateCommands = orderBy(Object.values(this.stages), [
            'order'
        ]).map(stage => () => stage.createCommands());
        sortedActionCreateCommands.forEach(createCommands => createCommands());
    }

    createStageCommands(stageName: string) {
        const stage = this.stages[stageName];
        const shouldReplaceAccumulateCommands =
            this.lastCreatedStageName === stageName &&
            stage.stageCommandsRelation === CommandsRelation.accumulate;

        if (shouldReplaceAccumulateCommands) {
            this.replaceAccumulateCommands();
        }

        if (!stage) {
            throw new Error(
                `The stage ${stageName} doesn't exist for ${this.name} action`
            );
        }

        if (stage.contextOverride) {
            this.setContext(stage.contextOverride);
        }

        this.lastCreatedStageName = stageName;
        stage.createCommands();
    }

    private replaceAccumulateCommands() {
        this.ctx.commands = this.ctx.commands.filter(
            x =>
                this.lastCreatedCommands.commands.findIndex(
                    y => y.id === x.id
                ) === -1
        );

        this.ctx.revertCommands = this.ctx.revertCommands.filter(
            x =>
                this.lastCreatedCommands.commands.findIndex(
                    y => y.id === x.id
                ) === -1
        );
    }

    @action
    applyLastStageCommands() {
        const stage = this.stages[this.lastCreatedStageName];

        this.lastAppliedStageName = this.lastCreatedStageName;

        if (stage.stageCommandsRelation === CommandsRelation.accumulate) {
            super.applyBulkActions(
                this.ctx.flow._id,
                this.lastCreatedCommands.commands
            );

            return;
        }

        super.applyCommands();
    }

    setContext(ctx: Partial<TContext>) {
        const { commands, revertCommands, ...restOfCtx } = ctx;
        this.handleCommandsResult(commands, revertCommands);

        if (commands) {
            this.lastCreatedCommands = {
                commands,
                revertCommands
            };
        }

        super.setContext(<Partial<TContext>>restOfCtx);
    }

    private handleCommandsResult(
        commands: TCommand[],
        revertCommands: TCommand[]
    ) {
        if (!commands && !revertCommands) {
            return;
        }

        this.ctx.commands = mergeCommands(
            commands,
            this.ctx.commands,
            this.lastCreatedStage.stageCommandsRelation
        );
        this.ctx.revertCommands = mergeCommands(
            revertCommands,
            this.ctx.revertCommands,
            this.lastCreatedStage.stageCommandsRelation
        );
    }
}
